import { msg } from "../locales/translate.ts";
import { LIVENESS_SERVICES, type TLivenessServiceName } from "./livenessData.ts";
import { parseServerDate } from "../utils/dateTime.ts";

export const LIVENESS_STATES = ["success", "failed", "missed", "pending", "degraded", "unknown"] as const;
export type AggregateState = typeof LIVENESS_STATES[number];
export type LivenessBucket = {
	from: number; to: number; expected: number; successful: number; failed: number; missed: number; state: AggregateState;
};
export type LivenessAggregate = {
	services: { service: TLivenessServiceName; buckets: LivenessBucket[]; state: AggregateState }[];
	bucketSeconds: number; from: number; to: number; now: number;
};
export const LIVENESS_COLORS: Record<AggregateState, string> = {
	success: "#287653", failed: "#b53b35", missed: "#d5ad20", pending: "#a6a5a2", degraded: "#db7b27", unknown: "#dedbd5",
};

// API date strings without an offset are interpreted as UTC by the server.
export const formatLivenessDate = (time: number) => new Date(time).toISOString().slice(0, 19).replace("T", " ");
export const livenessFilter = (hours: number, now = Date.now()) => ({
	from_date: formatLivenessDate(now - hours * 3_600_000), to_date: formatLivenessDate(now),
});

export function parseLivenessAggregate(payload: unknown): LivenessAggregate {
	const object = (value: unknown): Record<string, unknown> => {
		if (!value || typeof value !== "object" || Array.isArray(value)) throw new Error(msg("invalidAggregatedServiceStatus"));
		return value as Record<string, unknown>;
	};
	const date = (value: unknown) => {
		const result = typeof value === "string" ? (parseServerDate(value)?.getTime() ?? NaN) : NaN;
		if (!Number.isFinite(result)) throw new Error(msg("invalidServiceStatusTime"));
		return result;
	};
	const count = (value: unknown): number => {
		if (typeof value !== "number" || !Number.isSafeInteger(value) || value < 0) throw new Error(msg("invalidServiceStatusCount"));
		return value;
	};
	const envelope = object(payload);
	const root = "bucket" in envelope ? envelope : object(envelope.data);
	if (!Array.isArray(root.data) || typeof root.bucket !== "number" || !Number.isFinite(root.bucket) || root.bucket <= 0) throw new Error(msg("invalidServiceStatusIntervals"));
	const bucketSeconds = root.bucket;
	const range = object(root.timeRange);
	const from = date(range.from), to = date(range.to), now = date(range.now);
	if (to <= from) throw new Error(msg("invalidServiceStatusRange"));
	const groups = new Map<TLivenessServiceName, LivenessBucket[]>();
	for (const item of root.data) {
		const group = object(item);
		const service = group.service as TLivenessServiceName;
		if (!LIVENESS_SERVICES.includes(service) || groups.has(service) || !Array.isArray(group.data)) throw new Error(msg("invalidServiceStatusGroup"));
		const seen = new Set<number>();
		const buckets = group.data.map(item => {
			const row = object(item);
			const start = date(row.time);
			if (seen.has(start) || !LIVENESS_STATES.slice(0, -1).includes(row.status as AggregateState)) throw new Error("Invalid service status interval");
			seen.add(start);
			return { from: start, to: start + bucketSeconds * 1000, expected: count(row.expected), successful: count(row.successful), failed: count(row.failed), missed: count(row.missed), state: row.status as AggregateState };
		}).sort((a, b) => a.from - b.from);
		groups.set(service, buckets);
	}
	return { bucketSeconds, from, to, now, services: LIVENESS_SERVICES.map(service => {
		const buckets = groups.get(service) ?? [];
		// A still-running interval does not replace the last completed health result.
		const latest = [...buckets].reverse().find(bucket => bucket.state !== "pending") ?? buckets.at(-1);
		return { service, buckets, state: latest?.state ?? "unknown" };
	}) };
}

export function getAggregateState(data: LivenessAggregate | null): AggregateState {
	const states = data?.services.map(service => service.state) ?? [];
	for (const state of ["failed", "degraded", "missed", "unknown", "pending"] as const) if (states.includes(state)) return state;
	return states.length ? "success" : "unknown";
}
